import { useEffect, useState, useRef, useLayoutEffect } from "react";
import { domAnimation, LazyMotion, m } from "framer-motion";
import { useDeviceWidth } from "../../hooks/useDeviceWidth";
import Button from "../ui/Button";
import { Divider } from "../ui/Divider";
import React from "react";
import { Drawer } from "vaul";

const NavLink = ({
  children,
  href,
  className,
}: {
  children: React.ReactNode;
  href: string;
  className?: string;
}) => (
  <a
    href={href}
    className={`hover:bg-white-transparent rounded-xl active:scale-95 h-full w-27.5 py-1.5 flex justify-center items-center text-main transition-all duration-230 ease-in-out ${className}`}
  >
    {children}
  </a>
);

const MobileNavLink = ({
  children,
  href,
  onClick,
  className,
}: {
  children: React.ReactNode;
  href: string;
  onClick?: () => void;
  className?: string;
}) => (
  <a
    href={href}
    onClick={onClick}
    className={`w-full py-4 flex justify-center items-center text-title-mobile font-normal text-white-main ${className}`}
  >
    {children}
  </a>
);

export const Header = () => {
  const [showHeader, setShowHeader] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const lastProgrammaticScroll = useRef(0);
  const deviceWidth = useDeviceWidth();
  const isMobile = deviceWidth <= 1100;
  const triggerRef = useRef<HTMLElement | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useLayoutEffect(() => {
    const target = document.getElementById("cases");
    if (target) triggerRef.current = target;
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const now = performance.now();
      if (now - lastProgrammaticScroll.current < 200) return;
      if (isMobile && !("ontouchstart" in window)) return;

      const currentScrollY = window.scrollY;
      const scrollDelta = Math.abs(currentScrollY - lastScrollY);
      if (scrollDelta < 60) return;

      const triggerOffset = triggerRef.current?.offsetTop ?? Infinity;

      if (currentScrollY < triggerOffset) {
        if (currentScrollY > lastScrollY) {
          setShowHeader(false);
        } else {
          setShowHeader(true);
        }
      } else {
        setShowHeader(false);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollY, isMobile]);

  return !isMobile ? (
    <LazyMotion features={domAnimation}>
      <m.header
        initial={{ y: -120 }}
        animate={{ y: showHeader ? 0 : -120 }}
        transition={{ duration: 0.7, ease: [0.25, 0.4, 0.25, 1] }}
        className=" max-w-360 w-[calc(100vw-60px)] bg-blur rounded-2xl bg-white-transparent py-1.5 pr-1.5 pl-4 h-13 flex items-center justify-between fixed top-10 left-1/2 transform -translate-x-1/2 z-50"
      >
        <a
          href="#"
          className="font-magic text-[42px] leading-[70%] hover:text-white-main/80 transition-all duration-300"
        >
          avela
        </a>
        <div className="flex gap-3 h-full">
          <NavLink href="#services">Услуги</NavLink>
          <NavLink href="#stages">Этапы</NavLink>
          <NavLink href="#cases">Работы</NavLink>
          <NavLink href="#contact">Контакты</NavLink>
        </div>
      </m.header>
    </LazyMotion>
  ) : (
    <div className="absolute h-screen">
      <LazyMotion features={domAnimation}>
        <Drawer.Root shouldScaleBackground={true} open={isMenuOpen} onOpenChange={setIsMenuOpen}>
          <m.nav
            initial={{ y: 120 }}
            animate={{ y: showHeader ? 0 : 120 }}
            transition={{ duration: 0.7, ease: [0.45, 0.8, 0.5, 1] }}
            className="flex gap-3 h-fit fixed w-[calc(100%-40px)] top-[calc(100dvh-68px)] left-1/2 will-change-transform transform -translate-x-1/2 z-50"
          >
            <a
              href="#form"
              onClick={() => setIsMenuOpen(false)}
              className="min-w-[65%] w-full flex justify-center items-center sm:text-main text-main-mobile font-medium bg-white-main text-blue-main rounded-[14px] active:bg-white-main/80"
            >
              Связаться
            </a>
              <Button
                className="min-w-fit w-full bg-white-transparent bg-blur text-main-mobile text-white-main active:bg-white-transparent/80"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                Меню
              </Button>
          </m.nav>
          <Drawer.Portal>
            <Drawer.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-[2px]" />
            <Drawer.Content className="fixed bottom-0 left-0 flex flex-col gap-2 w-screen will-change-transform bg-blue-main rounded-t-4xl shadow-2xl px-6 py-8 h-[55vh] items-center justify-start outline-none">
              <MobileNavLink onClick={() => setIsMenuOpen(false)} href="#services">
                Услуги
              </MobileNavLink>
              <Divider />
              <MobileNavLink onClick={() => setIsMenuOpen(false)} href="#stages">
                Этапы
              </MobileNavLink>
              <Divider />
              <MobileNavLink onClick={() => setIsMenuOpen(false)} href="#cases">
                Работы
              </MobileNavLink>
              <Divider />
              <MobileNavLink onClick={() => setIsMenuOpen(false)} href="#contact">
                Контакты
              </MobileNavLink>
            </Drawer.Content>
          </Drawer.Portal>
        </Drawer.Root>
      </LazyMotion>
    </div>
  );
};
